import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Menu, Popover, Typography } from "antd";
import { Fragment, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteSupplier } from "../../redux/actions/supplier/deleteSupplierAction";
import { loadSupplier } from "../../redux/actions/supplier/detailSupplierAction";
import Loader from "../loader/loader";
import SupplierInvoiceTable, { DEFAULT_SUPPLIER_INVOICE_COLUMNS } from "../Card/SupplierInvoiceList";
import SupplierReturnInvoiceList from "./ListCard/SupplierReturnInvoiceList";
import SupplierTransactionList from "./ListCard/SupplierTransactionList";
import "./suppliers.css";

const TABLE_SCROLL_Y = 360;

const DetailsSup = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const supplier = useSelector((state) => state.suppliers.supplier);

  const buildColumns = () =>
    DEFAULT_SUPPLIER_INVOICE_COLUMNS.map((col) =>
      col.key === "id"
        ? { ...col, render: (id) => <Link to={`/purchase/${id}`}>{id}</Link> }
        : col
    );
  const initialColumns = buildColumns();
  const [columnItems, setColumnItems] = useState(() =>
    initialColumns.map((item) => ({
      key: item.key,
      label: <span>{item.title}</span>,
    }))
  );
  const [columnsToShow, setColumnsToShow] = useState(initialColumns);

  useEffect(() => {
    const withIdRender = buildColumns();
    setColumnsToShow(withIdRender);
    setColumnItems(
      withIdRender.map((item) => ({
        key: item.key,
        label: <span>{item.title}</span>,
      }))
    );
  }, []);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      setColumnsToShow((prev) => prev.filter((item) => item.key !== col.key));
    } else {
      const base = DEFAULT_SUPPLIER_INVOICE_COLUMNS.map((c) =>
        c.key === "id"
          ? { ...c, render: (id) => <Link to={`/purchase/${id}`}>{id}</Link> }
          : c
      );
      const foundIndex = base.findIndex((item) => item.key === col.key);
      const foundCol = base.find((item) => item.key === col.key);
      setColumnsToShow((prev) => {
        const next = [...prev];
        next.splice(foundIndex, 0, foundCol);
        return next;
      });
    }
  };

  const onDelete = () => {
    try {
      dispatch(deleteSupplier(id));
      setVisible(false);
      toast.warning(`Supplier : ${supplier.name} is removed `);
      return navigate("/supplier");
    } catch (error) {
      console.log(error.message);
    }
  };

  const [visible, setVisible] = useState(false);
  const handleVisibleChange = (newVisible) => setVisible(newVisible);

  useEffect(() => {
    dispatch(loadSupplier(id));
  }, [id]);

  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <div className="sup-detail-page">
      <div className="pcat-detail-wrap">
        {supplier ? (
          <Fragment key={supplier.id}>
            <Card bordered={false} className="sup-detail-card">
              <div className="sup-detail-header">
                <h5 className="sup-detail-title">
                  <i className="bi bi-person-lines-fill" aria-hidden />
                  <span className="sup-detail-name">{supplier.name}</span>
                </h5>
                <div className="sup-detail-actions">
                  <Link
                    to={`/supplier/${supplier.id}/update`}
                    state={{ data: supplier }}>
                    <Button type="primary" shape="round" icon={<EditOutlined />} aria-label="Edit supplier" />
                  </Link>
                  <Popover
                    content={
                      <a onClick={onDelete}>
                        <Button type="primary" danger>Yes Please !</Button>
                      </a>
                    }
                    title="Are you sure you want to delete ?"
                    trigger="click"
                    visible={visible}
                    onVisibleChange={handleVisibleChange}>
                    <Button type="danger" shape="round" icon={<DeleteOutlined />} aria-label="Delete supplier" />
                  </Popover>
                  {supplier?.purchaseInvoice && (
                    <>
                      <Dropdown
                        overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                        placement="bottomRight">
                        <Button size="small" className="column-visibility">Column Visibility</Button>
                      </Dropdown>
                      <CSVLink
                        data={supplier.purchaseInvoice}
                        className="btn btn-dark btn-sm"
                        filename={`supplier_${supplier.name}`}>
                        Download CSV
                      </CSVLink>
                    </>
                  )}
                </div>
              </div>

              <div className="sup-detail-info">
                <p>
                  <Typography.Text strong>Phone Number :</Typography.Text> {supplier.phone ?? "N/A"}
                </p>
                <p>
                  <Typography.Text strong>Address :</Typography.Text> {supplier.address ?? "—"}
                </p>
                <p>
                  <Typography.Text strong>Due Amount :</Typography.Text> {supplier.due_amount ?? "0"}
                </p>
                <p>
                  <Typography.Text strong>Opening Balance :</Typography.Text> {supplier.opening_balance ?? "0"}
                </p>
                <p>
                  <Typography.Text strong>Opening Balance Date :</Typography.Text>{" "}
                  {supplier.opening_balance_date ? new Date(supplier.opening_balance_date).toLocaleDateString() : "—"}
                </p>
              </div>

              <div className="sup-detail-section">
                <h5 className="sup-detail-section-heading">Invoices</h5>
                <div className="detail-page-table-wrap">
                  <SupplierInvoiceTable
                    list={supplier.purchaseInvoice}
                    linkTo="/purchase"
                    columns={columnsToShow.length ? columnsToShow : undefined}
                    scroll={{ x: true, y: TABLE_SCROLL_Y }}
                  />
                </div>
              </div>

              <SupplierReturnInvoiceList list={supplier?.allReturnPurchaseInvoice} />
              <SupplierTransactionList list={supplier?.allTransaction} />
            </Card>
          </Fragment>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
};

export default DetailsSup;
